/**
 * LyricsLookupPage
 *
 * Route: /lyrics-lookup
 *
 * Type to search Spotify tracks (debounced, uses the OAuth Bearer token via
 * SpotifyAuthInterceptor against api.spotify.com/v1/search).
 * Each result row has a "Lookup Lyrics" button that calls the
 * `getSpotifyLyrics` Firebase Function to fetch synced lyrics server-side.
 */

import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule, IonContent } from '@ionic/angular';
import { RouterModule } from '@angular/router';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { SpotifyAuthService } from '../../services/spotify-auth.service';

interface SpotifyTrack {
  id: string;
  name: string;
  artists: { name: string }[];
  album: { name: string; images: { url: string }[] };
  duration_ms: number;
}

interface LyricsLine {
  startTimeMs: string;
  words: string;
  endTimeMs: string;
}

@Component({
  selector: 'app-lyrics-lookup',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, RouterModule, JsonPipe],
  templateUrl: './lyrics-lookup.page.html',
  styleUrls: ['./lyrics-lookup.page.scss'],
})
export class LyricsLookupPage implements OnInit, OnDestroy {
  @ViewChild(IonContent) content!: IonContent;

  private functions  = inject(Functions);
  private http       = inject(HttpClient);
  private spotifyAuth = inject(SpotifyAuthService);

  // ─── Search state ──────────────────────────────────────────────────────────
  searchQuery    = '';
  searchResults: SpotifyTrack[] = [];
  searchLoading  = false;

  // ─── Lyrics state ─────────────────────────────────────────────────────────
  selectedTrack: SpotifyTrack | null = null;
  lyricsLoading  = false;
  lyricsError    = '';
  rawData: any   = null;
  lines: LyricsLine[] = [];
  syncType       = '';
  provider       = '';

  // ─── Lookup state per row (trackId → loading flag) ────────────────────────
  loadingTrackId = '';

  private searchSubject = new Subject<string>();
  private searchSub!: Subscription;

  get isAuthenticated(): boolean {
    return this.spotifyAuth.isAuthenticated();
  }

  login(): void {
    this.spotifyAuth.login();
  }

  ngOnInit(): void {
    // Wire up debounced search — 350 ms after the user stops typing
    this.searchSub = this.searchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query.trim()) {
          this.searchLoading = false;
          return of([]);
        }
        return this.http.get<any>(
          `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=track&limit=8`
        ).pipe(
          map(res => res.tracks?.items ?? []),
          catchError(() => {
            this.searchLoading = false;
            return of([]);
          })
        );
      })
    ).subscribe(tracks => {
      this.searchResults = tracks;
      this.searchLoading = false;
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  /** Called on every keypress in the search box */
  onSearchInput(): void {
    this.searchLoading = this.searchQuery.trim().length > 0;
    // Clear lyrics when user starts a new search
    if (this.searchQuery.trim().length === 0) {
      this.clearLyrics();
    }
    this.searchSubject.next(this.searchQuery);
  }

  /** Called when the user clicks "Lookup Lyrics" on a result row */
  async lookupLyrics(track: SpotifyTrack): Promise<void> {
    this.loadingTrackId = track.id;
    this.selectedTrack  = track;
    this.clearLyrics();

    try {
      const getLyrics = httpsCallable<{ trackId: string }, any>(
        this.functions,
        'getSpotifyLyrics'
      );
      const result = await getLyrics({ trackId: track.id });
      this.rawData = result.data;

      const lyrics   = this.rawData?.lyrics;
      this.lines     = lyrics?.lines    ?? [];
      this.syncType  = lyrics?.syncType ?? '';
      this.provider  = lyrics?.provider ?? '';

      // Scroll down to show lyrics
      setTimeout(() => this.content?.scrollToBottom(400), 50);

    } catch (err: any) {
      this.lyricsError = err?.message ?? 'Failed to fetch lyrics.';
    } finally {
      this.loadingTrackId = '';
    }
  }

  clearLyrics(): void {
    this.rawData      = null;
    this.lines        = [];
    this.syncType     = '';
    this.provider     = '';
    this.lyricsError  = '';
    this.selectedTrack = null;
  }

  /** Formats milliseconds as m:ss */
  formatTime(ms: string): string {
    const total = Math.floor(parseInt(ms, 10) / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /** Returns the smallest available album image URL */
  albumThumb(track: SpotifyTrack): string {
    const images = track.album?.images ?? [];
    return (images[images.length - 1] ?? images[0])?.url ?? '';
  }

  artistNames(track: SpotifyTrack): string {
    return track.artists.map(a => a.name).join(', ');
  }
}
